import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Typography,
} from '@mui/material';
import { ArrowForwardRounded, HistoryRounded } from '@mui/icons-material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import EmployeePortalClient, {
    EmployeeEmployerSummary,
    EmployeePortalSummary,
    EmployeeReviewSummary,
} from '../apiClients/EmployeePortalClient';
import BusySpinner from '../components/BusySpinner';
import { useErrorDisplay } from '../components/ErrorDisplay';
import PageTitle from '../components/PageTitle';
import PanelSegment from '../components/PanelSegment';
import { SessionStorageItemIds } from '../Enums';

export const chooseEmployment = (
    summary: EmployeePortalSummary,
    requestedEmploymentId?: string | null,
    storedEmploymentId?: string | null,
): EmployeeEmployerSummary | undefined => {
    const employers = summary.Employers ?? [];
    return employers.find(employer => employer.EmploymentId === requestedEmploymentId)
        ?? employers.find(employer => employer.EmploymentId === storedEmploymentId)
        ?? employers.find(employer => employer.EmploymentId === summary.DefaultEmploymentId)
        ?? employers.find(employer => employer.IsActive)
        ?? employers[0];
};

const formatDate = (value?: string | null) => {
    if (!value)
        return '—';
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleDateString();
};

const EmployeePortalPage = () => {
    const history = useHistory();
    const location = useLocation();
    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState(true);
    const [summary, setSummary] = useState<EmployeePortalSummary>({ Employers: [] });
    const [employmentId, setEmploymentId] = useState('');
    const [reviews, setReviews] = useState<EmployeeReviewSummary[]>([]);
    const summaryGeneration = useRef(0);
    const reviewGeneration = useRef(0);

    const requestedEmploymentId = useMemo(
        () => new URLSearchParams(location.search).get('employmentId'),
        [location.search],
    );

    useEffect(() => {
        const generation = ++summaryGeneration.current;
        const controller = new AbortController();
        setBusy(true);
        new EmployeePortalClient().GetSummary(controller.signal)
            .then(result => {
                if (generation !== summaryGeneration.current)
                    return;
                const selected = chooseEmployment(
                    result,
                    requestedEmploymentId,
                    localStorage.getItem(SessionStorageItemIds.LastEmployeeEmploymentId),
                );
                setSummary(result);
                setEmploymentId(selected?.EmploymentId ?? '');
                if (selected)
                    localStorage.setItem(SessionStorageItemIds.LastEmployeeEmploymentId, selected.EmploymentId);
                else
                    localStorage.removeItem(SessionStorageItemIds.LastEmployeeEmploymentId);
            })
            .catch(error => {
                if (generation === summaryGeneration.current && error?.name !== 'AbortError')
                    setError(error, 'We could not load your personal development portal.');
            })
            .finally(() => generation === summaryGeneration.current && setBusy(false));
        return () => {
            controller.abort();
            summaryGeneration.current += 1;
        };
    // setError is supplied by the shared error surface and is not a data dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestedEmploymentId]);

    useEffect(() => {
        const generation = ++reviewGeneration.current;
        setReviews([]);
        if (!employmentId) {
            setBusy(false);
            return;
        }

        const controller = new AbortController();
        setBusy(true);
        new EmployeePortalClient().GetReviews(employmentId, controller.signal)
            .then(result => generation === reviewGeneration.current && setReviews(result))
            .catch(error => {
                if (generation === reviewGeneration.current && error?.name !== 'AbortError')
                    setError(error, 'We could not load your published records.');
            })
            .finally(() => generation === reviewGeneration.current && setBusy(false));
        return () => {
            controller.abort();
            reviewGeneration.current += 1;
        };
    // setError is supplied by the shared error surface and is not a data dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employmentId]);

    const selectedEmployer = summary.Employers.find(employer => employer.EmploymentId === employmentId);
    const selectEmployer = (nextEmploymentId: string) => {
        if (!summary.Employers.some(employer => employer.EmploymentId === nextEmploymentId))
            return;
        setEmploymentId(nextEmploymentId);
        localStorage.setItem(SessionStorageItemIds.LastEmployeeEmploymentId, nextEmploymentId);
        history.replace(`/EmployeePortal?employmentId=${encodeURIComponent(nextEmploymentId)}`);
    };

    return <Box data-testid="employee-portal-page" sx={{ minWidth: 0 }}>
        <ErrorDisplay />
        <BusySpinner busy={busy}>
            <PanelSegment
                titleContent={<PageTitle title="My development records" />}
                toolbar={summary.Employers.length > 1 ? <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 260 } }}>
                    <InputLabel id="employee-employer-label">Employer history</InputLabel>
                    <Select
                        labelId="employee-employer-label"
                        label="Employer history"
                        value={employmentId}
                        onChange={event => selectEmployer(String(event.target.value))}
                    >
                        {summary.Employers.map(employer => <MenuItem key={employer.EmploymentId} value={employer.EmploymentId}>
                            {employer.OrganizationName}{employer.IsActive ? '' : ' — Former employer'}
                        </MenuItem>)}
                    </Select>
                </FormControl> : undefined}
            >
                <Stack spacing={2.5} sx={{ p: { xs: 1.5, sm: 2.5, md: 3 } }}>
                    {selectedEmployer && <Card variant="outlined" component="section" aria-label="Employment context">
                        <CardContent>
                            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} justifyContent="space-between" alignItems={{ sm: 'center' }}>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="h6" component="h2" fontWeight={800}>{selectedEmployer.OrganizationName}</Typography>
                                    <Typography color="text.secondary">{selectedEmployer.PositionTitle}</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                                        {selectedEmployer.IsActive
                                            ? `Active employment${selectedEmployer.StartedDate ? ` since ${formatDate(selectedEmployer.StartedDate)}` : ''}`
                                            : `Employment ended ${formatDate(selectedEmployer.EndedDate)}. Only records published while you were associated remain available.`}
                                    </Typography>
                                </Box>
                                <Chip
                                    label={selectedEmployer.IsActive ? 'Current employer' : 'Former employer'}
                                    color={selectedEmployer.IsActive ? 'success' : 'default'}
                                    variant={selectedEmployer.IsActive ? 'filled' : 'outlined'}
                                />
                            </Stack>
                        </CardContent>
                    </Card>}

                    {!selectedEmployer && <Alert severity="info">
                        No employer development history is available for this account.
                    </Alert>}

                    {selectedEmployer && <Box component="section" aria-labelledby="published-reviews-heading">
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                            <HistoryRounded color="primary" />
                            <Typography id="published-reviews-heading" variant="h6" component="h2" fontWeight={800}>Published development records</Typography>
                        </Stack>
                        {reviews.length === 0 ? <Alert severity="info">No published records are available for this employment.</Alert> :
                            <Grid container spacing={2}>
                                {reviews.map(review => <Grid item xs={12} md={6} lg={4} key={review.Id}>
                                    <Card variant="outlined" component="article" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                        <CardContent sx={{ flex: 1 }}>
                                            <Typography variant="h6" component="h3" fontWeight={750}>{review.TemplateName}</Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                Published {formatDate(review.PublishedDate)}
                                            </Typography>
                                            <Typography variant="h4" color="primary.main" sx={{ mt: 2 }}>
                                                {review.OverallScore}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">Overall score</Typography>
                                        </CardContent>
                                        <CardActions sx={{ justifyContent: 'flex-end', p: 1.5 }}>
                                            <Button
                                                endIcon={<ArrowForwardRounded />}
                                                onClick={() => history.push(`/EmployeePortal/Employment/${encodeURIComponent(employmentId)}/Reviews/${encodeURIComponent(review.Id)}`)}
                                                sx={{ minHeight: 44 }}
                                            >
                                                View development record
                                            </Button>
                                        </CardActions>
                                    </Card>
                                </Grid>)}
                            </Grid>}
                    </Box>}
                </Stack>
            </PanelSegment>
        </BusySpinner>
    </Box>;
};

export default EmployeePortalPage;
